import * as querystring from 'querystring'
import React, { useContext, useState, useEffect } from 'react'
import ProgressBar from '@ramonak/react-progress-bar'
import StepWhichUtility, { Utility } from './step-which-utility'
import StepCurrentSupplier, { Supplier } from './step-current-supplier'
import StepBusinessDetails from './step-business-details'
import StepAddress from './step-address'
import StepPersonalDetails from './step-personal-details'
import StepPersonalDetails2 from './step-personal-details-2'
import StepBusinessDetailsAndAddress2 from './step-business-details-and-address-2'
import StepCustomerType from './step-customer-type'
import StepSpend, { SpendAmount } from './step-spend'
import StepAffiliate, { AffiliateType } from './step-affiliate'
import StepHomeCustomer from './step-home-customer'
import StepBusinessDetailsAndAddress from './step-business-details-and-address'
import StepBusinessMinimal from './step-business-minimal'
import styles from './master-form.module.css'
import classNames from 'classnames'
import Target from '../../../content/assets/target.svg'
import LoaderButton from '../loader-button'
import { GlobalStateContext } from '../../context/GlobalContextProvider'
import { getIp } from '../../service/ip'
import { trackFormEvent } from '../../utils/tracking'
import { Confetti } from '../confetti'
import {
  FORM_CODE_DEFAULT,
  FORM_CODE_MINIMAL,
  FORM_CODE_ORIGINAL,
  FORM_CODE_POSTCODE_API,
} from '../query-params'
import { randInt } from '../../utils/math'
import { replaceSpecialCharacters } from '../../utils/string-utils'

const acceptTermsOptional = true

export const CustomerType = {
  Business: 'business',
  Home: 'home',
}

export const HomeCustomerType = {
  Regular: 'regular',
  Solar: 'solar',
}

const ErrorMessage = {
  EnterFullName: '* Please enter your full name',
  General: "Sorry, there's been an error. Please try again.",
  AcceptTerms: '* Please accept the T&Cs',
}

export const GoogleEvent = {
  Category: 'Form',
  Action: {
    HistoryPrefix: 'Energy Progress:',
    SelectedEnergyHome: 'Energy: Selected Home',
    SelectedEnergyBusiness: 'Energy: Selected Business',
    SubmitDuplicate: 'Submit Duplicate',
    SubmitSuccess: 'Submit Success',
    SubmitHomeAppend: '(home customer)',
    SubmitFailedValidation: 'Submit Failed on Validation',
    SubmitFailedUnknown: 'Submit Failed on Unknown',
  },
}

const submitUrl = '/.netlify/functions/form'

const formStepsCustomerType = [StepCustomerType]

const formStepsHome = [StepHomeCustomer, StepAffiliate]

const formStepsBusinessDefault = [
  StepWhichUtility,
  StepCurrentSupplier,
  // StepSpend,
  StepBusinessDetailsAndAddress,
  StepPersonalDetails,
]

const formStepsBusinessPostcodeApi = [
  StepWhichUtility,
  StepCurrentSupplier,
  StepSpend,
  StepBusinessDetailsAndAddress2,
  StepPersonalDetails2,
]

// minimal possible form
const formStepsBusinessMinimal = [StepBusinessMinimal]

/**
 * use the full name to 'create' the first name and last name fields
 */
export const finaliseName = (fullName) => {
  const splitName = fullName.trim().split(' ') // remove white space and split into words

  // after splitting we might have array items of '' if double spaces were between words - strip these out
  for (let i = splitName.length - 1; i >= 0; i--) {
    if (splitName[i] === '') {
      splitName.splice(i, 1)
    }
  }

  let lastName = splitName.length > 1 ? splitName.pop() : 'None' // use last word, or nothing if there aren't 2 words or more
  let firstName = splitName.join(' ') // use all remaining words as first names
  return {
    firstName: firstName,
    lastName: lastName,
  }
}

/**
 * we want the user to be able to enter nothing for the email address, but certain deliveries require it
 * build a placeholder email address we can use if nothing is supplied
 */
export const finaliseEmail = (email) => {
  if (email && email.length) {
    return email
  }
  const firstPart = ['hello', 'hi', 'mail', 'email', 'info']
  const lastPart = ['gmail.com', 'mail.com', 'email.com', 'yahoo.com', 'hotmail.com']
  return `${firstPart[randInt(0, firstPart.length)]}@${lastPart[randInt(0, lastPart.length)]}`
}

/**
 * sometimes the business name contains special characters (possibly from the address lookup)
 * strip out anything that might cause issues later
 */
export const finaliseBusinessName = (businessName) => {
  return replaceSpecialCharacters(businessName)
}

export const mapHomeCustomerTypeToAffiliateType = (homeCustomerType) => {
  switch (homeCustomerType) {
    case HomeCustomerType.Solar:
      return AffiliateType.Solar
    case HomeCustomerType.Regular:
    default:
      return AffiliateType.Energy
  }
}

const MasterForm = () => {
  const getFormSteps = (customerType, prependFormStepCustomerType) => {
    let steps
    let trackingAction
    if (customerType === CustomerType.Business) {
      trackingAction = GoogleEvent.Action.SelectedEnergyBusiness
      const formCode = globalState.formCode
      switch (formCode) {
        case FORM_CODE_MINIMAL:
          steps = formStepsBusinessMinimal
          break
        case FORM_CODE_POSTCODE_API:
          steps = formStepsBusinessPostcodeApi
          break
        case FORM_CODE_ORIGINAL:
        case FORM_CODE_DEFAULT:
        default:
          steps = formStepsBusinessDefault
          break
      }
    } else if (customerType === CustomerType.Home) {
      trackingAction = GoogleEvent.Action.SelectedEnergyHome
      steps = formStepsHome
    } else {
      return [...formStepsCustomerType] // return here, as we still do not know who the customer is so we need to find out
    }

    // track the energy type chosen
    trackEvent(GoogleEvent.Category, trackingAction)

    const finalSteps = prependFormStepCustomerType ? [...formStepsCustomerType, ...steps] : steps

    setStepNames(finalSteps.map((step) => getStepFriendlyName(step)))

    // return the steps, and prepend the type check step if requested
    return finalSteps
  }

  const initialCustomerType = ''
  const globalState = useContext(GlobalStateContext)
  const [formHistory, setFormHistory] = useState([])
  const [bestFormHistoryLength, setBestFormHistoryLength] = useState(0)
  const [steps, setSteps] = useState(getFormSteps(initialCustomerType, false))
  const [stepNames, setStepNames] = useState([])
  const [currentStep, setCurrentStep] = useState(0)
  const [form, setForm] = useState({
    customerType: initialCustomerType,
    homeCustomerType: '',
    utility: '',
    supplier: '',
    street1: '',
    city: '',
    postcode: '',
    businessName: '',
    spend: '',
    fullName: '',
    telephone: '',
    email: '',
    address: {
      label: null,
      businessName: null,
      street: null,
      city: null,
      postcode: null,
    },
    acceptTerms: false,
    errorMessage: '',
    showThanks: false,
    isLoading: false,
  })

  useEffect(() => {
    setStepNames(steps.map((step) => getStepFriendlyName(step)))
  }, [steps])

  useEffect(() => {
    // NOTE: only tracking business customer activity here
    form.customerType === CustomerType.Business &&
      setFormHistory(stepNames.slice(1, currentStep + 1)) // slice from 1, because 0 is StepCustomerType
  }, [currentStep])

  useEffect(() => {
    if (formHistory && formHistory.length) {
      const str = GoogleEvent.Action.HistoryPrefix + ' ' + formHistory.join(' > ')
      if (formHistory.length > bestFormHistoryLength) {
        setBestFormHistoryLength(formHistory.length)
        trackEvent(GoogleEvent.Category, str)
      }
    }
  }, [formHistory]) // only run this effect if formHistory changes

  const gotoNextStep = () => {
    let currStep = currentStep
    currStep = currStep === 0 || currStep < steps.length - 1 ? currStep + 1 : currStep
    setCurrentStep(currStep)
  }

  const gotoPreviousStep = () => {
    let currStep = currentStep
    currStep = currStep <= 1 ? 0 : currStep - 1
    setCurrentStep(currStep)
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm({
      ...form,
      [name]: value,
    })
    if (name === 'customerType') {
      // dynamically change the form based on the user's selection
      setSteps(getFormSteps(event.target.value, true))
    }
    if (
      name === 'customerType' ||
      name === 'homeCustomerType' ||
      name === 'utility' ||
      name === 'supplier' ||
      name === 'spend'
    ) {
      gotoNextStep()
    }
  }

  const handleAddressChange = (address) => {
    setForm({
      ...form,
      businessName: address.businessName || form.businessName,
      address,
    })
  }

  const handleAcceptTerms = () => {
    setForm({
      ...form,
      acceptTerms: !form.acceptTerms,
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (currentStep !== steps.length - 1) {
      return
    }

    if (!form.fullName) {
      setForm({
        ...form,
        isLoading: false,
        errorMessage: ErrorMessage.EnterFullName,
      })
      return
    }

    const { acceptTerms } = form
    if (acceptTerms || acceptTermsOptional) {
      setForm({
        ...form,
        isLoading: true,
      })
      try {
        const response = await submit()
        const json = await response.json()
        if (json.code === 1 || json.code === -2) {
          let action =
            json.code === -2 ? GoogleEvent.Action.SubmitDuplicate : GoogleEvent.Action.SubmitSuccess
          if (form.customerType === CustomerType.Home) {
            action += ` ${GoogleEvent.Action.SubmitHomeAppend}` // track home customers differently
          }
          trackEvent(GoogleEvent.Category, action, json.code)
          setFormHistory((prev) => [...prev, 'submit-success'])
          setForm({
            ...form,
            errorMessage: '',
            showThanks: true,
            isLoading: false,
          })
        } else {
          const errorString = json.errors.join(', ')
          // track as a validation error
          trackEvent(
            GoogleEvent.Category,
            GoogleEvent.Action.SubmitFailedValidation,
            json.code,
            errorString,
          )
          if (errorString.includes('1337')) {
            // specific error that we want to show a success message for, even though the request failed
            setForm({
              ...form,
              errorMessage: '',
              showThanks: true,
              isLoading: false,
            })
          } else {
            // normal error - present message to user to fix the issues
            setForm({
              ...form,
              isLoading: false,
              errorMessage: `* ${errorString}`,
            })
          }
        }
      } catch (e) {
        trackEvent(GoogleEvent.Category, GoogleEvent.Action.SubmitFailedUnknown, null, e)
        setForm({
          ...form,
          isLoading: false,
          errorMessage: ErrorMessage.General,
        })
      }
    } else {
      setForm({
        ...form,
        isLoading: false,
        errorMessage: ErrorMessage.AcceptTerms,
      })
    }
  }

  const trackEvent = (category, action, value, label) => {
    !isTestMode() && trackFormEvent(globalState.formCode, category, action, value, label)
  }

  const isTestMode = () => {
    return globalState.testMode === '1'
  }

  const submit = async () => {
    const {
      utility,
      supplier,
      street1,
      city,
      postcode,
      businessName,
      spend,
      fullName,
      telephone,
      email,
      address,
    } = form

    // normalise spend time periods to always be monthly
    const spendValue = finaliseSpend(spend)

    // if utility was not set then default it to 'both'
    const modifiedUtility = !utility ? Utility.Both : utility

    // use the supplier given, or set to a default value
    const modifiedSupplier = supplier || Supplier.Other

    const finalisedName = finaliseName(fullName)
    const finalisedEmail = finaliseEmail(email)
    const finalisedBusinessName = finaliseBusinessName(businessName)

    // attempt to get IP, ignore if fails
    const geo = await getIp()
    const ipAddress = geo && geo.ip_address

    const body = querystring.stringify({
      street1: address?.street || street1,
      towncity: address?.city || city,
      postcode: address?.postcode || postcode,
      company: finalisedBusinessName,
      phone1: telephone,
      first_name: finalisedName.firstName,
      last_name: finalisedName.lastName,
      email: finalisedEmail,
      utility: modifiedUtility,
      supplier: modifiedSupplier,
      spend: spendValue, // numeric, adjusted value
      spendOriginal: spend, // original string value

      ipaddress: ipAddress,

      ads_campaignid: globalState.campaignId,
      ads_adgroupid: globalState.adgroupId,
      ads_creative: globalState.creative,
      ads_matchtype: globalState.matchType,
      ads_keyword: globalState.keyword,

      utm_source: globalState.source,
      utm_medium: globalState.medium,
      utm_campaign: globalState.campaign,
      utm_content: globalState.content,

      formCode: globalState.formCode,

      testmode: isTestMode(),
    })

    isTestMode() && console.log('POST BODY', body)

    return await fetch(submitUrl, {
      method: 'POST',
      body: body,
    })
  }

  /*
   * convert the spend enum into a numeric value
   */
  const finaliseSpend = (spend) => {
    switch (spend) {
      case SpendAmount.DontKnow:
        return -2
      case SpendAmount.Below1000:
        return 0
      case SpendAmount.Between1000And5000:
        return 1000
      case SpendAmount.Over5000:
        return 5000
      default:
        return -1
    }
  }

  const getStepFriendlyName = (step) => {
    switch (step) {
      case StepAffiliate:
        return 'affiliate'
      case StepCustomerType:
        return 'customer-type'
      case StepHomeCustomer:
        return 'home-customer'
      case StepWhichUtility:
        return 'utility'
      case StepCurrentSupplier:
        return 'supplier'
      case StepBusinessDetails:
        return 'business-details-no-address'
      case StepSpend:
        return 'spend'
      case StepBusinessDetailsAndAddress:
      case StepBusinessDetailsAndAddress2:
        return 'business-details'
      case StepAddress:
        return 'address'
      case StepPersonalDetails:
      case StepPersonalDetails2:
        return 'personal-details'
      case StepBusinessMinimal:
        return 'minimal'
      default:
        'unknown-step'
    }
  }

  const renderHeading = () => {
    const { showThanks } = form
    if (!showThanks && currentStep === 0) {
      return (
        <div className={styles.questionIntro}>
          <p>Compare energy prices now.</p>
          <p>It only takes a minute.</p>
        </div>
      )
    }
    return null
  }

  const renderForm = () => {
    return (
      <>
        {renderStepCounter()}
        {renderProgressBar()}
        <form className={styles.form} onSubmit={handleSubmit}>
          {renderFormStep()}
        </form>
        <hr />
        {renderPreviousButton()}
        {renderNextButton()}
        {renderSubmitButton()}
        {renderSubmitNote()}
      </>
    )
  }

  const renderStepCounter = () => {
    if (steps[currentStep] === StepAffiliate || steps[currentStep] === StepHomeCustomer) return null
    if (currentStep !== 0) {
      return (
        <p className={styles.stepCounter}>
          {currentStep + 1}/{steps.length}
        </p>
      )
    }
  }

  const renderProgressBar = () => {
    if (steps[currentStep] === StepAffiliate || steps[currentStep] === StepHomeCustomer) return null
    const progressPercent = currentStep === 0 ? 0 : ((currentStep + 1) / steps.length) * 100
    return (
      <div className={styles.progressBar}>
        <ProgressBar
          bgColor="#281A36"
          borderRadius="4px"
          completed={progressPercent}
          isLabelVisible={false}
          height="8px"
          transitionDuration="0.5s"
          transitionTimingFunction="ease-in-out"
        />
      </div>
    )
  }

  const renderFormStep = () => {
    const step = steps[currentStep]
    switch (step) {
      case StepHomeCustomer:
        return (
          <StepHomeCustomer handleChange={handleChange} homeCustomerType={form.homeCustomerType} />
        )
      case StepAffiliate:
        return <StepAffiliate type={mapHomeCustomerTypeToAffiliateType(form.homeCustomerType)} />
      case StepCustomerType:
        return <StepCustomerType handleChange={handleChange} customerType={form.customerType} />
      case StepWhichUtility:
        return <StepWhichUtility handleChange={handleChange} utility={form.utility} />
      case StepCurrentSupplier:
        return <StepCurrentSupplier handleChange={handleChange} supplier={form.supplier} />
      case StepBusinessDetails:
        return <StepBusinessDetails handleChange={handleChange} businessName={form.businessName} />
      case StepSpend:
        return <StepSpend handleChange={handleChange} spend={form.spend} />
      case StepBusinessDetailsAndAddress:
        return (
          <StepBusinessDetailsAndAddress
            handleChange={handleChange}
            businessName={form.businessName}
            street1={form.street1}
            city={form.city}
            postcode={form.postcode}
          />
        )
      case StepBusinessDetailsAndAddress2:
        return (
          <StepBusinessDetailsAndAddress2
            handleAddressChange={handleAddressChange}
            address={form.address}
          />
        )
      case StepAddress:
        return (
          <StepAddress
            handleChange={handleChange}
            customerType={form.customerType}
            street1={form.street1}
            city={form.city}
            postcode={form.postcode}
          />
        )
      case StepPersonalDetails:
        return (
          <StepPersonalDetails
            handleChange={handleChange}
            handleAcceptTerms={handleAcceptTerms}
            email={form.email}
            fullName={form.fullName}
            telephone={form.telephone}
            acceptTerms={form.acceptTerms}
            errorMessage={form.errorMessage}
          />
        )
      case StepPersonalDetails2:
        return (
          <StepPersonalDetails2
            handleChange={handleChange}
            handleAcceptTerms={handleAcceptTerms}
            businessName={form.businessName}
            email={form.email}
            fullName={form.fullName}
            telephone={form.telephone}
            acceptTerms={form.acceptTerms}
            errorMessage={form.errorMessage}
          />
        )
      case StepBusinessMinimal:
        return (
          <StepBusinessMinimal
            handleChange={handleChange}
            handleAcceptTerms={handleAcceptTerms}
            email={form.email}
            fullName={form.fullName}
            telephone={form.telephone}
            businessName={form.businessName}
            postcode={form.postcode}
            acceptTerms={form.acceptTerms}
            errorMessage={form.errorMessage}
          />
        )
      default:
        return <></>
    }
  }

  const renderThanks = () => {
    return (
      <div className={styles.thanks}>
        <Confetti />
        <p className={styles.thanksHeading}>Thanks!</p>
        <Target className={styles.thanksImage} />
        <p className={styles.thanksMain}>
          Your information has been submitted successfully - now you can sit back and let us do the
          hard work!
        </p>
        <p className={styles.thanksSub}>
          We are now looking over your details and will get back to you very soon with quotes.
        </p>
        {/* <a className={classNames(styles.buttonClearFormatting, styles.thanksAffiliate)} href={Affiliates.Awin.BusinessBroadband}>
          <button className={styles.button} type='button'>Business Broadband</button>
        </a> */}
        {/* <hr className={styles.hrSmall}/>
        <a className={classNames(styles.buttonClearFormatting, styles.thanksAffiliate)} href={Affiliates.Awin.BusinessInsurance}>
          <button className={styles.button} type='button'>Business Insurance</button>
        </a> */}
      </div>
    )
  }

  const renderPreviousButton = () => {
    if (currentStep > 0) {
      return (
        <button
          className={classNames(styles.button, styles.previousButton)}
          disabled={form.isLoading}
          type="button"
          onClick={gotoPreviousStep}
        >
          Back
        </button>
      )
    }
    return null
  }

  const renderNextButton = () => {
    // always render the next button on first step (this could change in the future?)
    if (currentStep === 0 || currentStep < steps.length - 1) {
      return (
        <button
          className={styles.button}
          disabled={form.isLoading || !form.customerType.length}
          type="button"
          onClick={() => gotoNextStep(null)}
        >
          Next
        </button>
      )
    }
    return null
  }

  const renderSubmitButton = () => {
    if (steps[currentStep] === StepAffiliate || steps[currentStep] === StepHomeCustomer) {
      return null
    }
    // don't show the submit button on step 1 (this could change in the future?)
    if (currentStep !== 0 && currentStep === steps.length - 1) {
      return (
        <LoaderButton
          className={`${styles.button} ${styles.submitButton}`}
          isLoading={form.isLoading}
          onClick={handleSubmit}
        >
          {isTestMode() ? 'Submit TEST' : 'Get Quotes'}
        </LoaderButton>
      )
    }
    return null
  }

  const renderSubmitNote = () => {
    return steps[currentStep] === StepPersonalDetails ||
      steps[currentStep] === StepPersonalDetails2 ? (
      <p className={styles.acceptTermsLegal}>
        By clicking &quot;Get Quotes&quot; you agree to our{' '}
        <a href={'/terms/'} target="_blank" rel="noreferrer" className={styles.termsLink}>
          terms
        </a>{' '}
        and to be contacted regarding your quote.
      </p>
    ) : null
  }

  return (
    <div className={styles.wrapper}>
      <div id="form" className={styles.fixerContainer}>
        {!form.showThanks && renderHeading()}
        {form.showThanks ? renderThanks() : renderForm()}
      </div>
    </div>
  )
}

export default MasterForm
